import { useState } from 'react';
import { competitorBenchmark } from '@/mocks/contentEngineStrategy';

export default function CompetitorSocialReferences() {
  const { competitors, brandMetrics, gapAnalysis, contentRecommendations } = competitorBenchmark;
  const [selectedMetric, setSelectedMetric] = useState('visibility');

  const allPlayers = [
    { name: 'Your Brand', ...brandMetrics, isBrand: true },
    ...competitors.map((c) => ({ name: c.name, ...c, isBrand: false })),
  ];

  const metrics = [
    { key: 'visibility', label: 'Visibility Score', max: 100 },
    { key: 'followers', label: 'Followers', max: 25000 },
    { key: 'engagement', label: 'Engagement Rate (%)', max: 6 },
    { key: 'postsPerWeek', label: 'Posts/Week', max: 10 },
  ];

  const selectedMetricData = metrics.find((m) => m.key === selectedMetric)!;

  return (
    <div className="space-y-6 w-full">
      {/* Competitor Comparison Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-900">
            <i className="ri-bar-chart-grouped-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Brand vs Competitor Overview</h3>
            <p className="text-sm text-gray-500">Side-by-side metrics across all tracked competitors</p>
          </div>
        </div>

        {/* Metric Selector */}
        <div className="flex flex-wrap gap-2 mb-5">
          {metrics.map((m) => (
            <button
              key={m.key}
              onClick={() => setSelectedMetric(m.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                selectedMetric === m.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Comparison Bars */}
        <div className="space-y-4">
          {allPlayers.map((player) => {
            const val = player[selectedMetric as keyof typeof player] as number;
            const pct = (val / selectedMetricData.max) * 100;
            return (
              <div key={player.name} className="flex items-center gap-4">
                <div className="w-32 flex-shrink-0">
                  <p className={`text-sm font-semibold ${player.isBrand ? 'text-blue-700' : 'text-gray-700'}`}>
                    {player.isBrand ? 'Your Brand' : player.name}
                  </p>
                </div>
                <div className="flex-1">
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${player.isBrand ? 'bg-blue-600' : 'bg-gray-400'}`}
                      style={{ width: `${Math.max(pct, 5)}%` }}
                    />
                  </div>
                </div>
                <div className="w-24 text-right flex-shrink-0">
                  <p className={`text-sm font-bold ${player.isBrand ? 'text-blue-700' : 'text-gray-700'}`}>
                    {selectedMetric === 'followers'
                      ? val.toLocaleString()
                      : selectedMetric === 'engagement'
                        ? `${val}%`
                        : val}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Score Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Visibility', brand: brandMetrics.visibility, comp: Math.round(competitors.reduce((s, c) => s + c.visibility, 0) / competitors.length) },
          { label: 'Followers', brand: brandMetrics.followers, comp: Math.round(competitors.reduce((s, c) => s + c.followers, 0) / competitors.length) },
          { label: 'Engagement', brand: brandMetrics.engagement, comp: (competitors.reduce((s, c) => s + c.engagement, 0) / competitors.length).toFixed(1) },
          { label: 'Post Frequency', brand: brandMetrics.postsPerWeek, comp: Math.round(competitors.reduce((s, c) => s + c.postsPerWeek, 0) / competitors.length) },
          { label: 'Avg Reach', brand: brandMetrics.avgReach, comp: Math.round(competitors.reduce((s, c) => s + c.avgReach, 0) / competitors.length) },
        ].map((metric) => {
          const b = Number(metric.brand);
          const c = Number(metric.comp);
          const diff = b - c;
          const diffPct = c > 0 ? ((diff / c) * 100).toFixed(0) : '0';
          return (
            <div key={metric.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{metric.label}</p>
              <div className="flex items-end gap-2 mb-2">
                <span className="text-2xl font-bold text-gray-900">{metric.brand}</span>
                <span className={`text-xs font-bold mb-1 ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {diff >= 0 ? '+' : ''}
                  {diffPct}%
                </span>
              </div>
              <p className="text-xs text-gray-500">vs competitor avg ({metric.comp})</p>
            </div>
          );
        })}
      </div>

      {/* Gap Analysis */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-5">Opportunity Gaps</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Area</th>
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">Your Brand</th>
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">Competitor Avg</th>
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">Gap</th>
                <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-center">Priority</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gapAnalysis.map((gap) => (
                <tr key={gap.area} className="hover:bg-gray-50">
                  <td className="py-3 text-sm font-medium text-gray-900">{gap.area}</td>
                  <td className="py-3 text-sm text-gray-700 text-right">{gap.brand}</td>
                  <td className="py-3 text-sm text-gray-700 text-right">{gap.competitorAvg}</td>
                  <td className="py-3 text-sm font-semibold text-right">
                    <span className={gap.gap.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}>{gap.gap}</span>
                  </td>
                  <td className="py-3 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded text-xs font-bold ${
                        gap.priority === 'high'
                          ? 'bg-red-50 text-red-700'
                          : gap.priority === 'medium'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                      }`}
                    >
                      {gap.priority.toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Content Recommendations */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/10">
            <i className="ri-lightbulb-flash-line text-white text-lg"></i>
          </div>
          <h3 className="text-lg font-bold text-white">Strategic Content Recommendations</h3>
        </div>
        <div className="space-y-3">
          {contentRecommendations.map((rec, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">{i + 1}</span>
              </div>
              <p className="text-sm text-gray-300">{rec}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}